import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Brightness4,
  Brightness7,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useTheme as useAppTheme } from '../../../hooks/useTheme';
import { UserMenu } from '../UserMenu/UserMenu';
import { HeaderProps } from '../layout.types';

export const Header: React.FC<HeaderProps> = ({
  drawerOpen,
  onDesktopDrawerToggle,
  onMobileDrawerToggle,
  drawerWidth,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const { user } = useAuth();
  const { themeMode, toggleTheme } = useAppTheme();
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          width: { 
            xs: '100%',
            md: drawerOpen ? `calc(100% - ${drawerWidth}px)` : '100%'
          },
          ml: { 
            xs: 0,
            md: drawerOpen ? `${drawerWidth}px` : 0
          },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          {/* Mobile menu button */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onMobileDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Desktop menu button */}
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={onDesktopDrawerToggle}
            sx={{ mr: 2, display: { xs: 'none', md: 'block' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {t(`navigation.${location.pathname.slice(1)}`) || t('navigation.dashboard')}
          </Typography>

          {/* Theme Toggle */}
          <IconButton color="inherit" onClick={toggleTheme}>
            {themeMode === 'dark' ? React.createElement(Brightness7) : React.createElement(Brightness4)}
          </IconButton>

          {/* User Avatar */}
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenuClick}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.name?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      <UserMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      />
    </>
  );
};
